import { Paginator } from "@/components/paginator";
import { SelectNumberOfRows } from "./-components/select-number-of-rows";
import {
  PaymentsColumnSettings,
  usePersistedPaymentsColumnVisibility,
} from "./-components/payments-column-settings";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, numberToDecimalWithCurrency } from "@/lib/utils";
import { VolumeApi } from "@/lib/volume-api";
import { Payouts } from "@/types/payments";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { z } from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { AuthorityRequired } from "@/lib/auth";
import { Spinner } from "@/components/ui/spinner";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import { CardWithHeader } from "@/components/card-with-header";
import { GenerateReport } from "./-components/generate-report";
import { PayoutStatementDialog } from "./-components/payout-statement-dialog";
import { PayoutFilters } from "./-components/payout/filters";
import {
  normalizePaymentsGlobalSearch,
  paymentsGlobalSearchParamSchema,
} from "./-components/global-search";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type HeaderGroup,
  type Row,
  type VisibilityState,
} from "@tanstack/react-table";

const defaultFromDate = dayjs()
  .subtract(30, "day")
  .startOf("day")
  .toISOString();
const defaultToDate = dayjs().endOf("day").toISOString();
const defaultCurrency = "GBP";
export const defaultRows = 10;
const defaultPage = 1;

const searchParams = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().optional(),
  currency: z.string().optional(),
  rows: z.number().optional(),
  searchFilter: paymentsGlobalSearchParamSchema,
  amountFrom: z.string().optional(),
  amountTo: z.string().optional(),
  statuses: z.string().optional(),
});

export const allPayoutStatuses = [
  "PROCESSED",
  "IN_PROGRESS",
  "FAILED",
  "CANCELLED",
  "HELD",
  "RETURNED",
] as const;
export type PayoutStatus = (typeof allPayoutStatuses)[number];
export const payoutStatusMapping = (status: PayoutStatus): string => {
  switch (status) {
    case "PROCESSED":
      return "Processed";
    case "IN_PROGRESS":
      return "In Progress";
    case "FAILED":
      return "Failed";
    case "CANCELLED":
      return "Cancelled";
    case "HELD":
      return "Held";
    case "RETURNED":
      return "Returned";
  }
};

type Payout = Payouts["content"][number];

const payoutColumnLabels = {
  payoutId: "Payout ID",
  status: "Status",
  createdAt: "Payout Initiated",
  amount: "Amount",
  reference: "Reference",
  recipientAccountNumber: "Recipient Account Number",
  recipientSortCode: "Recipient Sort Code",
  recipientIban: "Recipient IBAN",
} satisfies Record<string, string>;

export const Route = createFileRoute(
  "/(app)/_main-layout/_payments/payments/payout",
)({
  component: () => <PayoutsComponent />,
  validateSearch: zodValidator(searchParams),
  loaderDeps: ({
    search: {
      from,
      page,
      to,
      currency,
      rows,
      searchFilter,
      amountFrom,
      amountTo,
      statuses,
      applicationId,
    },
  }) => ({
    from,
    page,
    to,
    currency,
    rows,
    searchFilter,
    amountFrom,
    amountTo,
    statuses,
    applicationId,
  }),
  loader: async ({
    context,
    deps: {
      from,
      page,
      to,
      currency,
      rows,
      searchFilter,
      amountFrom,
      amountTo,
      statuses,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      applicationId,
    },
  }) => {
    const api = new VolumeApi(
      context.accessToken,
      context.selectedApplicationId,
    );
    const globalSearch = normalizePaymentsGlobalSearch(searchFilter);
    const isSearching = Boolean(globalSearch);
    return api.getPayouts({
      merchantId: context.merchantId,
      page: ((page ?? defaultPage) - 1).toString(),
      size: (rows ?? defaultRows).toString(),
      sort: "createdAt,desc",
      ...(isSearching
        ? { globalSearch }
        : {
            ...(currency ? { currency } : { currency: defaultCurrency }),
            startDateTimeUtc: from ?? defaultFromDate,
            endDateTimeUtc: to ?? defaultToDate,
            ...(amountFrom && { minAmount: Number(amountFrom) }),
            ...(amountTo && { maxAmount: Number(amountTo) }),
            ...(statuses && { statuses }),
          }),
    });
  },
  pendingComponent: () => (
    <div className="ml-4">
      Please wait. Fetching Payouts may take up to 10 seconds.
      <Spinner width={40} height={40} />
    </div>
  ),
});

const PayoutsComponent = () => {
  const context = Route.useRouteContext();
  const search = Route.useSearch();
  const payouts = Route.useLoaderData();
  const selectedApplication = useSelectedApplication();
  const tableDataKey = [
    payouts.number,
    payouts.size,
    payouts.totalElements,
    ...payouts.content.map((payout) => payout.externalProviderPaymentId),
  ].join(":");

  const startDateTimeUtc = search.from ?? defaultFromDate;
  const endDateTimeUtc = search.to ?? defaultToDate;

  if (!selectedApplication.hasVirtualAccountsEnabled) {
    return <Navigate to="/payments/payin" />;
  }

  return (
    <CardWithHeader
      variant="exchange"
      label="Pay Outs"
      title="Track outgoing payouts and generate statements"
    >
      <div className="flex">
        <div className="mb-2">
          <h3 className="text-base font-semibold text-volume-text-purple">
            Find Payouts
          </h3>
          <p className="text-sm text-slate-600">
            Search by a unique identifier <b>or</b> use filters to narrow down
            results
          </p>
        </div>
        <div className="ml-auto">
          <AuthorityRequired
            authoritiesRequired={["GENERATE_REPORTS"]}
            authAuthorities={context.authorities}
          >
            <GenerateReport
              startDateTimeUtc={startDateTimeUtc}
              endDateTimeUtc={endDateTimeUtc}
              reportType={"payout"}
              merchantId={context.merchantId}
              currency={search.currency}
            />
          </AuthorityRequired>
        </div>
      </div>
      <PayoutsTableContent key={tableDataKey} payouts={payouts} />
    </CardWithHeader>
  );
};

function PayoutsTableContent({ payouts }: { payouts: Payouts }) {
  const context = Route.useRouteContext();
  const hasRecipientAccSortCodeColumns =
    atLeastOneRecipientAccSortCodeColumn(payouts);
  const hasRecipientIBANColumn = atLeastOneRecipientIBANColumn(payouts);

  const recipientAccSortCodeColumns: ColumnDef<Payout>[] =
    hasRecipientAccSortCodeColumns
      ? [
          {
            id: "recipientAccountNumber",
            enableHiding: true,
            header: "Recipient Account Number",
            cell: ({ row }) => row.original.beneficiary.accountNumber,
          },
          {
            id: "recipientSortCode",
            enableHiding: true,
            header: "Recipient Sort Code",
            cell: ({ row }) => row.original.beneficiary.sortCode,
          },
        ]
      : [];
  const recipientIbanColumns: ColumnDef<Payout>[] = hasRecipientIBANColumn
    ? [
        {
          id: "recipientIban",
          enableHiding: true,
          header: "Recipient IBAN",
          cell: ({ row }) => row.original.beneficiary.iban,
        },
      ]
    : [];

  const columns: ColumnDef<Payout>[] = [
    {
      id: "payoutId",
      enableHiding: true,
      header: "Payout ID",
      cell: ({ row }) => row.original.externalProviderPaymentId,
      meta: { cellClassName: "whitespace-nowrap font-mono text-sm" },
    },
    {
      id: "status",
      enableHiding: true,
      header: "Status",
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="flex flex-wrap items-center gap-2 overflow-hidden">
            <span
              className={cn(
                "shrink-0 rounded-xl border border-current/15 px-2 py-[2px] text-center",
                payment.status === "PROCESSED" &&
                  "bg-emerald-100 text-emerald-800",
                payment.status === "FAILED" && "bg-rose-100 text-rose-800",
                payment.status === "CANCELLED" &&
                  "bg-violet-100 text-violet-800",
                payment.status === "HELD" && "bg-amber-100 text-amber-800",
                payment.status === "RETURNED" &&
                  "bg-orange-100 text-orange-800",
                payment.status === "IN_PROGRESS" &&
                  "bg-volume-light-purple/70 text-volume-text-purple",
              )}
            >
              {payment.status === "IN_PROGRESS"
                ? "In Progress"
                : payment.status.substring(0, 1) +
                  payment.status.substring(1).toLowerCase()}
            </span>
            {payment.statusTags.map((tag) => (
              <span
                key={tag}
                className="shrink-0 rounded-xl border border-violet-200 bg-violet-100 px-2 py-[2px] text-center text-violet-800"
              >
                {tag}
              </span>
            ))}
          </div>
        );
      },
      meta: { cellClassName: "overflow-hidden align-top" },
    },
    {
      id: "createdAt",
      enableHiding: true,
      header: "Payout Initiated",
      cell: ({ row }) =>
        new Date(row.original.createdAt).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "numeric",
        }),
    },
    {
      id: "amount",
      enableHiding: true,
      header: "Amount",
      cell: ({ row }) =>
        numberToDecimalWithCurrency(row.original.amount, row.original.currency),
      meta: { cellClassName: "whitespace-nowrap font-medium" },
    },
    {
      id: "reference",
      enableHiding: true,
      header: "Reference",
      cell: ({ row }) =>
        row.original.reference ? (
          <div
            className="truncate font-mono text-sm"
            title={row.original.reference}
          >
            {row.original.reference}
          </div>
        ) : (
          <span className="text-slate-400">-</span>
        ),
      meta: { cellClassName: "min-w-0 max-w-[180px] text-left" },
    },
    ...recipientAccSortCodeColumns,
    ...recipientIbanColumns,
    {
      id: "actions",
      enableHiding: false,
      header: () => null,
      cell: ({ row }) =>
        row.original.status === "PROCESSED" ? (
          <PayoutStatementDialog payout={row.original} />
        ) : null,
    },
  ];

  const { columnVisibility, setColumnVisibility, resetColumnVisibility } =
    usePersistedPaymentsColumnVisibility({
      tableId: "payouts",
      userId: context.userId,
      applicationId: context.selectedApplicationId,
    });
  // TanStack Table returns non-memoizable functions; no compiler-compatible API exists.
  // eslint-disable-next-line react-hooks-js/incompatible-library
  const table = useReactTable({
    data: payouts.content,
    columns,
    getRowId: (row) => row.externalProviderPaymentId,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });
  const visibleColumnCount = table
    .getAllLeafColumns()
    .filter((column) => columnVisibility[column.id] !== false).length;

  return (
    <>
      <PayoutFilters
        rightControl={
          <PaymentsColumnSettings
            table={table}
            labels={payoutColumnLabels}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
            onReset={resetColumnVisibility}
          />
        }
      />

      <div className="mt-3 overflow-hidden rounded-xl border border-volume-text-purple/20 bg-white">
        <div className="overflow-x-auto">
          <Table className="min-w-[700px] w-full bg-white">
            <PayoutsTableHeader
              headerGroups={table.getHeaderGroups()}
              columnVisibility={columnVisibility}
            />
            <PayoutsTableBody
              rows={table.getRowModel().rows}
              columnVisibility={columnVisibility}
              isEmpty={payouts.content.length === 0}
              visibleColumnCount={visibleColumnCount}
            />
          </Table>
        </div>
      </div>
      <div className="flex justify-center items-center mt-6">
        <div>
          <div className="mr-auto">
            Number of payouts: {payouts.totalElements}
          </div>
        </div>
        <div className="m-auto">
          <Paginator from={Route.id} totalPages={payouts.totalPages} />
        </div>
        <SelectNumberOfRows type={"payout"} />
      </div>
    </>
  );
}

function PayoutsTableHeader({
  headerGroups,
  columnVisibility,
}: {
  headerGroups: HeaderGroup<Payout>[];
  columnVisibility: VisibilityState;
}) {
  return (
    <TableHeader className="bg-slate-50/70">
      {headerGroups.map((headerGroup) => (
        <TableRow key={headerGroup.id} className="border-slate-200">
          {headerGroup.headers
            .filter((header) => columnVisibility[header.column.id] !== false)
            .map((header) => (
              <TableHead key={header.id} className="text-slate-600">
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
        </TableRow>
      ))}
    </TableHeader>
  );
}

function PayoutsTableBody({
  rows,
  columnVisibility,
  isEmpty,
  visibleColumnCount,
}: {
  rows: Row<Payout>[];
  columnVisibility: VisibilityState;
  isEmpty: boolean;
  visibleColumnCount: number;
}) {
  return (
    <TableBody>
      {isEmpty ? (
        <TableRow className="border-slate-200">
          <TableCell
            colSpan={visibleColumnCount}
            className="py-8 text-center text-slate-600"
          >
            No Pay Outs found
          </TableCell>
        </TableRow>
      ) : null}
      {rows.map((row) => (
        <TableRow
          key={row.id}
          className="border-slate-200 hover:bg-volume-light-purple/10"
        >
          {row
            .getAllCells()
            .filter((cell) => columnVisibility[cell.column.id] !== false)
            .map((cell) => {
              const meta = cell.column.columnDef.meta;
              return (
                <TableCell key={cell.id} className={meta?.cellClassName}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
        </TableRow>
      ))}
    </TableBody>
  );
}

function atLeastOneRecipientIBANColumn(payouts: Payouts) {
  return payouts.content.some((x) => x.beneficiary.iban !== null);
}
function atLeastOneRecipientAccSortCodeColumn(payouts: Payouts) {
  return (
    payouts.content.some((x) => x.beneficiary.accountNumber !== null) ||
    payouts.content.some((x) => x.beneficiary.sortCode !== null)
  );
}
